import { useMemo, useState } from "react";
import styled from "styled-components";
import { useAppSelector } from "../app/hooks";
import { profileState } from "../app/reducers/profile-slice";
import ReportMetricCard from "../components/report/ReportMetricCard";
import {
  useSLAByScheduleLineQuery,
  useSLAByWorkerQuery,
  useSLAIssuesSummaryQuery,
  useSLASummaryQuery,
} from "../query/report-query";
import { logisticsTypeLabel } from "../types/delivery/logistics-type";
import { SLAPeriodPayload } from "../types/report/sla";

const toDateString = (d: Date) => d.toISOString().slice(0, 10);
const startOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};
const formatPercent = (rate: number) => `${(rate * 100).toFixed(1)}%`;
const formatSeconds = (sec: number | null) => {
  if (sec === null) return "—";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}분`;
  const h = Math.floor(min / 60);
  const rem = min % 60;
  return rem ? `${h}시간 ${rem}분` : `${h}시간`;
};

const ReportView = () => {
  const { profile } = useAppSelector(profileState);
  const [fromDate, setFromDate] = useState(toDateString(startOfMonth()));
  const [toDate, setToDate] = useState(toDateString(new Date()));

  // Per-company scope is forced backend-side via user_context,
  // so we just pass the period; no need to attach clientId/agencyId.
  const payload: SLAPeriodPayload = useMemo(
    () => ({ fromDate, toDate }),
    [fromDate, toDate],
  );

  const summaryQuery = useSLASummaryQuery(payload);
  const byWorkerQuery = useSLAByWorkerQuery(payload);
  const byLineQuery = useSLAByScheduleLineQuery(payload);
  const issuesQuery = useSLAIssuesSummaryQuery(payload);

  const summary = summaryQuery.data?.data;
  const byWorker = byWorkerQuery.data?.data;
  const byLine = byLineQuery.data?.data;
  const issues = issuesQuery.data?.data;

  return (
    <Container>
      <Header>
        <Title>SLA 리포트</Title>
        <CompanyName>{profile?.company?.name ?? ""}</CompanyName>
      </Header>

      <PeriodRow>
        <DateInput
          type="date"
          value={fromDate}
          max={toDate}
          onChange={(e) => setFromDate(e.target.value)}
        />
        <Separator>~</Separator>
        <DateInput
          type="date"
          value={toDate}
          min={fromDate}
          max={toDateString(new Date())}
          onChange={(e) => setToDate(e.target.value)}
        />
      </PeriodRow>

      <Section>
        <SectionTitle>전체 지표</SectionTitle>
        <CardsRow>
          <ReportMetricCard
            label="총 배송 건수"
            value={(summary?.totalCount ?? 0).toLocaleString()}
            hint={`완료 ${(summary?.completedCount ?? 0).toLocaleString()}건`}
          />
          <ReportMetricCard
            label="정시 수행률 (일반 배송)"
            value={formatPercent(summary?.deliveryOnTimeRate ?? 0)}
            hint={`정시 ${summary?.deliveryOnTimeCount ?? 0} / 지연 ${
              summary?.deliveryDelayedCount ?? 0
            }`}
            tone="positive"
          />
          <ReportMetricCard
            label="지연률 (일반 배송)"
            value={formatPercent(summary?.deliveryDelayRate ?? 0)}
            hint={`평균 지연 ${formatSeconds(
              summary?.deliveryAverageDelaySeconds ?? null,
            )}`}
            tone={
              (summary?.deliveryDelayRate ?? 0) > 0.1 ? "negative" : "neutral"
            }
          />
          <ReportMetricCard
            label="수거/반납 당일 완료율"
            value={formatPercent(summary?.pickUpAndReturnSameDayRate ?? 0)}
            hint={`${summary?.pickUpAndReturnSameDayCount ?? 0} / ${
              summary?.pickUpAndReturnTotalCount ?? 0
            }`}
          />
        </CardsRow>
      </Section>

      <Section>
        <SectionTitle>물류 유형별</SectionTitle>
        <Table>
          <thead>
            <tr>
              <Th>유형</Th>
              <Th align="right">총건수</Th>
              <Th align="right">완료</Th>
              <Th align="right">정시</Th>
              <Th align="right">지연</Th>
              <Th align="right">평균 지연</Th>
            </tr>
          </thead>
          <tbody>
            {summary
              ? Object.values(summary.breakdownByLogisticsType).map((row) => (
                  <tr key={row.logisticsType}>
                    <Td>
                      {logisticsTypeLabel[
                        row.logisticsType as keyof typeof logisticsTypeLabel
                      ] ?? row.logisticsType}
                    </Td>
                    <Td align="right">{row.totalCount}</Td>
                    <Td align="right">{row.completedCount}</Td>
                    <Td align="right">{row.onTimeCount}</Td>
                    <Td align="right">{row.delayedCount}</Td>
                    <Td align="right">
                      {formatSeconds(row.averageDelaySeconds)}
                    </Td>
                  </tr>
                ))
              : null}
          </tbody>
        </Table>
      </Section>

      <TwoColumn>
        <Section>
          <SectionTitle>배송원별 (정시율 낮은 순)</SectionTitle>
          <Table>
            <thead>
              <tr>
                <Th>배송원</Th>
                <Th align="right">총건수</Th>
                <Th align="right">정시율</Th>
                <Th align="right">평균 지연</Th>
              </tr>
            </thead>
            <tbody>
              {byWorker?.rows.slice(0, 20).map((row) => (
                <tr key={row.workerId ?? "unassigned"}>
                  <Td>{row.workerName ?? "(미배정)"}</Td>
                  <Td align="right">{row.totalCount}</Td>
                  <Td align="right">{formatPercent(row.onTimeRate)}</Td>
                  <Td align="right">
                    {formatSeconds(row.averageDelaySeconds)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Section>

        <Section>
          <SectionTitle>라인별 (정시율 낮은 순)</SectionTitle>
          <Table>
            <thead>
              <tr>
                <Th>날짜</Th>
                <Th align="right">총건수</Th>
                <Th align="right">정시율</Th>
                <Th align="right">평균 지연</Th>
              </tr>
            </thead>
            <tbody>
              {byLine?.rows.slice(0, 20).map((row) => (
                <tr key={row.scheduleId ?? "x"}>
                  <Td>{row.dateToWork}</Td>
                  <Td align="right">{row.totalCount}</Td>
                  <Td align="right">{formatPercent(row.onTimeRate)}</Td>
                  <Td align="right">
                    {formatSeconds(row.averageDelaySeconds)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Section>
      </TwoColumn>

      <Section>
        <SectionTitle>
          이슈 로그 ({issues?.totalCount ?? 0}건)
        </SectionTitle>
        <Table>
          <thead>
            <tr>
              <Th>이슈 유형</Th>
              <Th align="right">건수</Th>
            </tr>
          </thead>
          <tbody>
            {issues?.countsByType.map((row) => (
              <tr key={row.issueType}>
                <Td>{row.issueType}</Td>
                <Td align="right">{row.count}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Section>
    </Container>
  );
};

export default ReportView;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px 32px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: ${({ theme }) => theme.palette.foundation.primaryA};
  margin: 0;
`;

const CompanyName = styled.div`
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  color: ${({ theme }) => theme.palette.primitives.gray600};
`;

const PeriodRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Separator = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.palette.primitives.gray500};
`;

const DateInput = styled.input`
  font-size: 13px;
  border: 1px solid ${({ theme }) => theme.palette.primitives.gray200};
  border-radius: 6px;
  padding: 6px 10px;
  background: #ffffff;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: ${({ theme }) => theme.palette.primitives.gray700};
  margin: 0;
`;

const CardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.palette.primitives.gray200};
  font-size: 13px;
`;

const Th = styled.th<{ align?: "left" | "right" }>`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  text-align: ${({ align }) => align ?? "left"};
  color: ${({ theme }) => theme.palette.primitives.gray700};
  background: ${({ theme }) => theme.palette.primitives.gray50};
  padding: 8px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.primitives.gray200};
`;

const Td = styled.td<{ align?: "left" | "right" }>`
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  text-align: ${({ align }) => align ?? "left"};
  color: ${({ theme }) => theme.palette.foundation.primaryA};
  padding: 8px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.primitives.gray100};
`;
